import axios, { AxiosPromise } from 'axios';
import dayjs from 'dayjs';
import { TripAssistiveDeviceJourneyDto } from '../models/device';
import { FormPlanTrip, TripTypeChoice } from '../models/form-plan-trip';
import { JourneyDto, JourneyKeyDto } from '../models/journey';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const journeyApi = {
  getCustomerJourneys(
    customerId: string,
    ScheduleType?: string,
    JourneyType?: string,
    Culture?: string,
  ): AxiosPromise<{ journeys: JourneyDto[] }> {
    return axios({
      method: 'GET',
      baseURL: API_BASE_URL,
      url: `/customers/${customerId}/journeys`,
      params: {
        Culture,
        ScheduleType,
        JourneyType,
      }
    });
  },
  planJourneys(
    customerId: number,
    Form: FormPlanTrip,
    Culture?: string
  ): AxiosPromise<{ journeys: Array<JourneyDto> }> {
    const requestedDateTime = dayjs(Form.Date)
      .set('hour', dayjs(Form.Time).hour())
      .set('minute', dayjs(Form.Time).minute())
      .set('second', 0);
    const requestedReturnDateTime = dayjs(Form.ReturnDate)
      .set('hour', dayjs(Form.ReturnTime).hour())
      .set('minute', dayjs(Form.ReturnTime).minute())
      .set('second', 0);
    const journeys = 
    [
      {
        origin: {
          addressIdentifier: Form.PointA?.addressLocation.addressIdentifier,
          landmarkIdentifier:Form.PointA?.addressLocation.landmarkIdentifier,
          // registeredAddressType: "Home",
          civicNumber: Form.PointA?.addressLocation.civicNumber,
          street: Form.PointA?.addressLocation.streetName,
          municipalityName: Form.PointA?.addressLocation.municipalityName,
          isServiceAddress: Form.PointA?.addressLocation.isServiceAddress,
          longitude: Form.PointA?.addressLocation.longitude,
          latitude: Form.PointA?.addressLocation.latitude,
          registeredAddressType: Form.PointA?.addressLocation?.registeredAddressType,
          streetType:Form.PointA?.addressLocation.streetType,
          streetOrientation: Form.PointA?.addressLocation?.streetOrientation
        },
        destination: {
          addressIdentifier: Form.PointB?.addressLocation.addressIdentifier,
          landmarkIdentifier:Form.PointB?.addressLocation.landmarkIdentifier,
          // registeredAddressType: "Null",
          civicNumber: Form.PointB?.addressLocation.civicNumber,
          street: Form.PointB?.addressLocation.streetName,
          municipalityName: Form.PointB?.addressLocation.municipalityName,
          isServiceAddress: Form.PointB?.addressLocation.isServiceAddress,
          longitude: Form.PointB?.addressLocation.longitude,
          latitude: Form.PointB?.addressLocation.latitude,
          registeredAddressType: Form.PointB?.addressLocation.registeredAddressType,
          streetType: Form.PointB?.addressLocation.streetType,
          streetOrientation: Form.PointB?.addressLocation?.streetOrientation
        },
        requestedDateTime: requestedDateTime.format(
          'YYYY-MM-DDTHH:mm:ss.SSS[Z]'
        ),
        requestedTimeType: Form.HourType.toString(),
        dayType: requestedDateTime.locale('en').format('dddd'),
        assistiveDevices: Form.AssistiveDevices?.map(
          (item: string): TripAssistiveDeviceJourneyDto => {
            return {
              id: item,
              numberForCustomer: 1,
              totalNumber: 1,
              description: item,
            };
          }
        ),
        tripPassengers: [...Form.TripPassengers, 'Customer'],
      }
    ];
    if (Form.TripType === TripTypeChoice.RoundTrip) {
      journeys.push(
        {
          origin: {
            addressIdentifier: Form.PointB?.addressLocation.addressIdentifier,
            landmarkIdentifier:Form.PointB?.addressLocation.landmarkIdentifier,
            // registeredAddressType: "Null",
            civicNumber: Form.PointB?.addressLocation.civicNumber,
            street: Form.PointB?.addressLocation.streetName,
            municipalityName: Form.PointB?.addressLocation.municipalityName,
            isServiceAddress: Form.PointB?.addressLocation.isServiceAddress,
            longitude: Form.PointB?.addressLocation.longitude,
            latitude: Form.PointB?.addressLocation.latitude,
            registeredAddressType: Form.PointB?.addressLocation.registeredAddressType,
            streetType: Form.PointB?.addressLocation.streetType,
            streetOrientation: Form.PointB?.addressLocation?.streetOrientation
          },
          destination: {
            addressIdentifier: Form.PointA?.addressLocation.addressIdentifier,
            landmarkIdentifier:Form.PointA?.addressLocation.landmarkIdentifier,
            // registeredAddressType: "Home",
            civicNumber: Form.PointA?.addressLocation.civicNumber,
            street: Form.PointA?.addressLocation.streetName,
            municipalityName: Form.PointA?.addressLocation.municipalityName,
            isServiceAddress: Form.PointA?.addressLocation.isServiceAddress,
            longitude: Form.PointA?.addressLocation.longitude,
            latitude: Form.PointA?.addressLocation.latitude,
            registeredAddressType: Form.PointA?.addressLocation?.registeredAddressType,
            streetType: Form.PointA?.addressLocation?.streetType,
            streetOrientation: Form.PointA?.addressLocation?.streetOrientation
          },
          
          requestedDateTime: requestedReturnDateTime.format(
            'YYYY-MM-DDTHH:mm:ss.SSS[Z]'
          ),
          requestedTimeType: Form.ReturnHourType.toString(),
          dayType: requestedReturnDateTime.locale('en').format('dddd'),
          assistiveDevices: Form.AssistiveDevices?.map(
            (item: string): TripAssistiveDeviceJourneyDto => {
              return {
                id: item,
                numberForCustomer: 1,
                totalNumber: 1,
                description: item,
              };
            }
          ),
          tripPassengers: [...Form.TripPassengers, 'Customer'],
        }
      );
    }
    return axios({
      method: 'POST',
      baseURL: API_BASE_URL,
      url: `/customers/${customerId}/journeys/planJourneys`,
      params: {
        Culture,
      },
      data: journeys
    });
  },
  cancelJourney(customerId: string, keys:Array<JourneyKeyDto>, Culture?: string): AxiosPromise<void> {
    return axios({
      method: 'PUT',
      baseURL: API_BASE_URL,
      url: `/customers/${customerId}/journeys/cancelJourney`,
      params: { Culture },
      data: keys
    });
  },
  saveJourney(
    customerId: number,
    journey: Array<JourneyDto>,
    Culture?: string
  ): AxiosPromise<{ journeys: Array<JourneyDto> }> {
    return axios({
      method: 'POST',
      baseURL: API_BASE_URL,
      url: `/customers/${customerId}/journeys/saveJourneys`,
      params: {
        Culture,
      },
      data: journey,
    });
  },
  confirmJourney(
    customerId: number,
    keys:Array<JourneyKeyDto>,
    Culture?: string
  ): AxiosPromise<{ journeys: Array<JourneyDto> }> {
    return axios({
      method: 'PUT',
      baseURL: API_BASE_URL,
      url: `/customers/${customerId}/journeys/confirmJourney`,
      params: {
        Culture,
      },
      data: keys
    });
  },
};

export default journeyApi;
